import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { chatService } from '../../services/supabase';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/supabase';

type ConversationRow = Database['public']['Tables']['ai_chat_conversations']['Row'];
type MessageRow = Database['public']['Tables']['ai_chat_messages']['Row'];

interface ChatState {
  conversations: ConversationRow[];
  currentConversationId: string | null;
  messages: MessageRow[];
  loading: boolean;
  sending: boolean;
  streamingContent: string;
  error: string | null;
}

const initialState: ChatState = {
  conversations: [],
  currentConversationId: null,
  messages: [],
  loading: false,
  sending: false,
  streamingContent: '',
  error: null,
};

export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async (userId: string) => {
    return chatService.listConversations(userId);
  }
);

export const startNewConversation = createAsyncThunk(
  'chat/startNewConversation',
  async ({ userId, organizationId }: { userId: string; organizationId: string }) => {
    return chatService.createConversation(userId, organizationId);
  }
);

export const selectConversation = createAsyncThunk(
  'chat/selectConversation',
  async (conversationId: string) => {
    const messages = await chatService.getMessages(conversationId);
    return { conversationId, messages };
  }
);

export const deleteConversation = createAsyncThunk(
  'chat/deleteConversation',
  async (conversationId: string) => {
    await chatService.deleteConversation(conversationId);
    return conversationId;
  }
);

/**
 * Persists the user's message, streams the assistant's reply from
 * /api/ai/chat, then persists the assistant's message once the stream
 * completes. The streaming endpoint is POST-only, and browser EventSource
 * only supports GET, so this reads the response body directly instead.
 */
export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (
    { conversationId, content, history }: { conversationId: string; content: string; history: MessageRow[] },
    { dispatch, getState, rejectWithValue }
  ) => {
    const userMessage = await chatService.addMessage(conversationId, 'user', content);
    dispatch(chatSlice.actions.messageAdded(userMessage));

    // First message names the conversation (else every sidebar entry reads
    // "New conversation" forever). Best-effort - don't block the send.
    const state = getState() as { chat: ChatState };
    const conversation = state.chat.conversations.find((c) => c.id === conversationId);
    if (conversation && conversation.title === 'New conversation') {
      const title = content.length > 60 ? `${content.slice(0, 57)}...` : content;
      dispatch(chatSlice.actions.conversationRenamed({ id: conversationId, title }));
      chatService.updateConversationTitle(conversationId, title).catch((err) => {
        console.error('Failed to persist conversation title:', err);
      });
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      return rejectWithValue('Not signed in');
    }

    const apiUrl = import.meta.env.VITE_API_URL || '';
    const response = await fetch(`${apiUrl}/api/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        messages: [
          ...history.map((m) => ({ role: m.role, content: m.content })),
          { role: 'user', content },
        ],
        streaming: true,
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      return rejectWithValue(data.message || data.error || `Request failed (${response.status})`);
    }
    if (!response.body) {
      return rejectWithValue('No response body from server');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullResponse = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const payload = JSON.parse(line.slice(6));
        if (payload.chunk) {
          fullResponse += payload.chunk;
          dispatch(chatSlice.actions.streamingChunkReceived(payload.chunk));
        } else if (payload.error) {
          // Terminal SSE error event from the server (mid-stream AI failure)
          // - surface it instead of persisting a truncated reply.
          return rejectWithValue(payload.error);
        } else if (payload.done) {
          fullResponse = payload.response ?? fullResponse;
        }
      }
    }

    const assistantMessage = await chatService.addMessage(conversationId, 'assistant', fullResponse);
    return assistantMessage;
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    messageAdded: (state, action) => {
      state.messages.push(action.payload);
    },
    streamingChunkReceived: (state, action) => {
      state.streamingContent += action.payload;
    },
    conversationRenamed: (state, action) => {
      const conversation = state.conversations.find((c) => c.id === action.payload.id);
      if (conversation) conversation.title = action.payload.title;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load conversations';
      })

      .addCase(startNewConversation.fulfilled, (state, action) => {
        state.conversations.unshift(action.payload);
        state.currentConversationId = action.payload.id;
        state.messages = [];
      })
      .addCase(startNewConversation.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to start a new conversation';
      })

      .addCase(selectConversation.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentConversationId = action.meta.arg;
      })
      .addCase(selectConversation.fulfilled, (state, action) => {
        // Stale-response guard: clicking A then B can resolve out of order,
        // so only apply messages for the conversation that's still selected.
        if (state.currentConversationId !== action.payload.conversationId) return;
        state.loading = false;
        state.messages = action.payload.messages;
      })
      .addCase(selectConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load conversation';
      })

      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.conversations = state.conversations.filter((c) => c.id !== action.payload);
        if (state.currentConversationId === action.payload) {
          state.currentConversationId = null;
          state.messages = [];
        }
      })
      .addCase(deleteConversation.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete conversation';
      })

      .addCase(sendMessage.pending, (state) => {
        state.sending = true;
        state.streamingContent = '';
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sending = false;
        state.streamingContent = '';
        state.messages.push(action.payload);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sending = false;
        state.streamingContent = '';
        state.error = (action.payload as string) || action.error.message || 'Failed to send message';
      });
  },
});

export const { clearError } = chatSlice.actions;
export default chatSlice.reducer;
